import { useEffect, useState } from "react";

import { isValid } from "date-fns";
import { useTranslation } from "react-i18next";
import Loading from "react-loading";

import { Drawer, Grid } from "components";

import { useToast } from "hooks/toast";

import { useAppSelector } from "store/rootReducer";

import { LoadSpinner } from "features/LoadSpinner";

import { AdminArea } from "components/Body/AdminArea";
import DateInput from "components/DateInput";
import GeneralInput from "components/GeneralInput";

import { IntegrationService } from "services/integrations";
import { SystemConfigurationService } from "services/systemConfiguration";

import { FORMS_ADMIN } from "utils/entities/permission";
import { formatDDMMYYYY } from "utils/format";

import { ContainerLoading } from "./styles";

export interface IServiceIntegrationImportationItem {
  hearingTypeId: number;
  hearingTypeName: string;
  active: boolean;
  attendantRoleList: Array<IDefaultObject>;
}

export interface IDefaultObject {
  ServiceIntegrationImportationId?: number;
  id: number;
  name: string;
  active: boolean;
  attendantRoleId: number;
  hearingTypeId: number;
}

export interface IServiceIntegrationImportationResponseAPI {
  customerId: number;
  hearingType: Array<IDefaultObject>;
  attendantRole: Array<IDefaultObject>;
  ServiceIntegrationImportation: Array<IServiceIntegrationImportationItem>;
}

export interface IIntegrationImportation {
  expecteddate: string;
  placeid: any;
}

export const ServiceIntegrationImportation = () => {
  const { t } = useTranslation();
  const [openModal, setOpenModal] = useState(false);
  const [filterList, setFilterList] = useState<any>([]);
  const [inputValues, setInputValues] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState(false);
  const [
    loadingModalServiceIntegrationImportation,
    setLoadingModalServiceIntegrationImportation,
  ] = useState(false);
  const [submited, setSubmited] = useState(false);
  const { addToast } = useToast();
  const { userCustomerProfile } = useAppSelector((state) => state.profile);

  const { user } = useAppSelector((state) => state.user);

  useEffect(() => {
    getAllConfigurationsByCustomerId();
  }, []);

  const getAllConfigurationsByCustomerId = async () => {
    setLoading(true);
    const { data } = await SystemConfigurationService.getAllByCustomer(
      user?.customerId,
    );
    const list = Array.isArray(data)
      ? data
          .filter((item: any) => item.name === "ServiceIntegrationImportation")
          .map((item: any) => ({
            ...item,
            description: t("administration.integrationImportation.title"),
          }))
      : [];
    setFilterList(list);

    const initialValues: { [key: string]: string } = {};
    list.forEach((item: any) => {
      try {
        const value = JSON.parse(item.value);
        value.parametros.forEach((param: any) => {
          const inputId = `${item.name}-${param.name}`;
          if (param.name === "placeid") {
            initialValues[inputId] = userCustomerProfile.placeId;
          } else if (param.name === "customerid") {
            initialValues[inputId] = user?.customerId;
          } else {
            initialValues[inputId] = "";
          }
        });
      } catch {
        // skip items with invalid JSON
      }
    });
    setInputValues(initialValues);
    setLoading(false);
  };

  const onclose = () => {
    getAllConfigurationsByCustomerId();
    setOpenModal(false);
    setSubmited(false);
    setInputValues({});
  };

  const checkInputsFilled = async () => {
    setSubmited(true);
    try {
      const allFilled = () => {
        for (const key in inputValues) {
          if (!inputValues[key]) {
            return false;
          }
        }
        return true;
      };

      if (allFilled()) {
        setLoadingModalServiceIntegrationImportation(true);
        const response = await importIntegration();

        if (response === undefined) {
          return;
        } else if (response?.status === 200) {
          addToast({
            type: "success",
            title: t("administration.integrationImportation.title"),
            description: response.data?.message,
          });
          setOpenModal(false);
        } else {
          addToast({
            type: "error",
            title: t("administration.integrationImportation.title"),
            description: response?.data?.message,
          });
          return;
        }
      } else {
        addToast({
          type: "warning",
          title: t("administration.integrationImportation.title"),
          description: t("forms.validations.required"),
        });
      }
    } finally {
      setLoadingModalServiceIntegrationImportation(false);
    }
  };

  const importIntegration = async () => {
    const [, firstValue] = Object.entries(inputValues)[0];

    const regex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
    if (!regex.test(firstValue)) {
      addToast({
        type: "warning",
        title: t("administration.integrationImportation.title"),
        description: t("forms.validations.invalidFormat"),
      });
      return;
    }

    const match = firstValue.match(regex);

    if (match) {
      const day = parseInt(match[1], 10);
      const month = parseInt(match[2], 10);
      const year = parseInt(match[3], 10);

      const today = new Date();
      const currentYear = today.getFullYear();

      if (
        month < 1 ||
        month > 12 ||
        day < 1 ||
        day > 31 ||
        year < currentYear - 10 ||
        year > currentYear + 10 ||
        new Date(year, month - 1, day).getDate() !== day
      ) {
        addToast({
          type: "warning",
          title: t("administration.integrationImportation.title"),
          description: t("forms.validations.invalidFormat"),
        });
        return;
      }

      const transformedValue = `${year}-${String(month).padStart(
        2,
        "0",
      )}-${String(day).padStart(2, "0")}`;

      const body = {
        expecteddate: transformedValue,
        placeid: userCustomerProfile.placeId,
      };

      return await IntegrationService.import(body);
    }
  };

  const formatDateISO8601 = (date: string) => {
    if (!date) return date;
    const parts = date.split("/").map(Number);

    if (parts.length !== 3 || parts.some(isNaN)) {
      return date;
    }
    const [day, month, year] = parts;
    if (month < 1 || day < 1) {
      return date;
    }

    return `${String(year).padStart(4, "0")}-${String(month).padStart(
      2,
      "0",
    )}-${String(day).padStart(2, "0")}`;
  };

  const validDate = (date: any) => {
    const isoDate = formatDateISO8601(date);
    const parsedDate = new Date(isoDate);
    return isValid(parsedDate);
  };

  return (
    <>
      <AdminArea
        loading={loading}
        title={t("administration.integrationImportation.title")}
        InputSearchLabel={t("common.search")}
        list={filterList}
        headerTrashButtonOnClick={() => {}}
        onClick={() => setOpenModal(true)}
        setItemReturnClick={[]}
        setOpenModal={openModal}
        permissionName={FORMS_ADMIN.SERVICE_INTEGRATION_IMPORTATION}
        onChangeSituation={() => null}
        searchText={""}
        showStatus={false}
        displayAtivoFilter={false}
        description={true}
        showSearch={false}
      />
      <Drawer
        open={openModal}
        onClose={() => onclose()}
        title={t("administration.integrationImportation.title")}
        onApply={() => checkInputsFilled()}
        paddingLeft={33}
        paddingRight={33}
      >
        {loading ? (
          <ContainerLoading>
            <Loading width={40} type="spin" color="#000" />
          </ContainerLoading>
        ) : (
          <Grid container spacing={3}>
            {filterList?.map((item: any, index: any) => {
              let value;
              try {
                value = JSON.parse(item.value);
              } catch {
                return null;
              }
              return (
                <Grid item xs={12} key={index}>
                  {value.parametros.map((param: any, paramIndex: any) => {
                    const inputId = `${item?.name}-${param?.name}`;
                    const isAutoFilled =
                      param.name === "placeid" || param.name === "customerid";

                    if (param.type === "date") {
                      return (
                        <Grid item xs={12} key={paramIndex}>
                          <DateInput
                            label={t("audiences.dateTime")}
                            setValue={(data: any) =>
                              setInputValues((prevValues) => ({
                                ...prevValues,
                                [inputId]: formatDDMMYYYY(data),
                              }))
                            }
                            value={
                              inputValues[inputId]
                                ? formatDateISO8601(inputValues[inputId])
                                : ""
                            }
                            required={true}
                            error={submited && !validDate(inputValues[inputId])}
                          />
                        </Grid>
                      );
                    }

                    return (
                      <Grid item xs={12} key={paramIndex}>
                        <GeneralInput
                          id={inputId}
                          error={
                            submited && !isAutoFilled && !inputValues[inputId]
                          }
                          value={inputValues[inputId] || ""}
                          required
                          onChange={(e) => {
                            setInputValues((prevValues) => ({
                              ...prevValues,
                              [inputId]: e,
                            }));
                          }}
                          name={param?.name}
                          label={param.label}
                          disabled={isAutoFilled}
                        />
                      </Grid>
                    );
                  })}
                </Grid>
              );
            })}
          </Grid>
        )}
      </Drawer>
      <LoadSpinner
        loading={loadingModalServiceIntegrationImportation}
        zindex={2000}
      />
    </>
  );
};
